import { QueryClient } from '@tanstack/react-query';
import { isAuthError, isConnectivityError } from '@/lib/api/errors';

/**
 * One QueryClient for the app. The API client already retries reads on a
 * short backoff through deploy windows, so Query's own retry only covers what
 * that ladder cannot: it never retries auth failures or 4xx answers, and it
 * stops once the health poller has taken over (connectivity errors) — the
 * poller refetches active queries on recovery. A busy read (429 from an
 * admission gate) has already been retried by the client's own longer ladder,
 * so it is not retried again here: the next refetch (focus, interval) asks anew.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        if (isAuthError(error) || isConnectivityError(error)) return false;
        const status = (error as { status?: number } | null)?.status;
        if (typeof status === 'number' && status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
    },
    mutations: { retry: false },
  },
});
